import { ReactNode, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentThumbnail,
  MiButton,
  MiChip,
  MiIcon,
  MiMenu,
  MiPopper,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  PERMISSIONS,
} from "@miplatform/constants";
import { getCurrencyString, toUpperCase } from "@miplatform/utils";
import {
  mdiAccountHardHatOutline,
  mdiInformationSlabCircleOutline,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";

export function ThinMaterialCard({
  item = {},
  menuData = [],
  leftAction,
  isSuppliedByBuilder = false,
  isLoading = false,
  isRemoved = false,
  cardStyle,
  showSalePrice = false,
}: ThinMaterialCardProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);

  const { classes, theme, cx } = useStyles({ cardStyle });
  const priceLabel = showSalePrice ? "Sale:" : "Cost:";
  const priceValue = showSalePrice ? item.netSalesPrice : item.cost;

  const getItemLabel = () => {
    const partNumber = item.manufacturerPartNumber ?? "";
    const desc = item.category?.itemDescription ?? item.itemDescription ?? "";
    return `${toUpperCase(partNumber)} | ${toUpperCase(desc)}`;
  };

  return (
    <Box className={classes.thinCard}>
      <Box className={classes.thinCardHeader}>
        <Box className={classes.cardContent}>
          {leftAction}
          <Typography
            variant="caption"
            className={cx(
              classes.itemLabel,
              isRemoved && classes.strikeThrough
            )}
          >
            {getItemLabel()}
          </Typography>
        </Box>
        <MiButton
          size="small"
          className={classes.infoButton}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
          startIcon={{ path: mdiInformationSlabCircleOutline, iconSize: 0.6 }}
          onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
          onMouseLeave={() => setAnchorEl(null)}
        />
      </Box>
      {menuData.length > 0 && (
        <MiMenu
          data={menuData}
          showHover
          testID={`material-card-${item.id}`}
          disabled={isLoading}
        />
      )}
      <MiPopper
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        placement="right"
        sx={{
          paddingBottom: theme.spacing(1),
          background: theme.palette.common.white,
          boxShadow: theme.shadows[1],
          border: `1px solid ${theme.palette.grey.darkAccent}`,
          borderRadius: 1,
        }}
      >
        <Box className={classes.popperHeader}>
          <Typography variant="body2" fontWeight={600}>
            {item.category?.categoryName}
          </Typography>
          {canReadPricing && (
            <Box className={classes.popperCostBox}>
              <Typography variant="caption" className={classes.costLabel}>
                {priceLabel}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                className={
                  isSuppliedByBuilder
                    ? classes.strikeThrough
                    : classes.costLabel
                }
              >
                {getCurrencyString(priceValue)}
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={classes.popperBody}>
          <DocumentThumbnail document={item.document} documentCount={1} />
          <Box className={classes.attributesBox}>
            {isSuppliedByBuilder && (
              <MiChip
                label="Builder Supplied"
                size="small"
                className={classes.builderSuppliedChip}
                icon={
                  <MiIcon
                    path={mdiAccountHardHatOutline}
                    size={0.6}
                    color={theme.palette.secondary.main}
                  />
                }
              />
            )}
            {item.attributes?.map((attr) => (
              <Box key={attr.id || attr.attributeId}>
                <Typography variant="caption" fontWeight={600}>
                  {attr.attributeName}:
                </Typography>{" "}
                <Typography variant="caption">{attr.value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </MiPopper>
    </Box>
  );
}

const useStyles = makeStyles<{ cardStyle?: React.CSSProperties }>()(
  (theme, { cardStyle }) => ({
    thinCard: {
      width: "100%",
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: 32,
      padding: theme.spacing(0, 2),
      margin: theme.spacing(0, 0, 0, 1),
      ...cardStyle,
    },
    thinCardHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    cardContent: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    itemLabel: {
      color: theme.palette.primary.main,
      fontWeight: theme.fontWeight.semiBold,
      margin: theme.spacing(0, 1),
      lineHeight: 1,
    },
    infoButton: {
      backgroundColor: theme.palette.primary.medium,
      maxWidth: 24,
      minWidth: 24,
      height: 16,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    popperHeader: {
      borderRadius: "4px 4px 0 0",
      display: "flex",
      backgroundColor: theme.palette.grey.accent,
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1.5, 2),
    },
    popperCostBox: {
      backgroundColor: theme.palette.secondary.light,
      display: "flex",
      alignItems: "center",
      borderRadius: 4,
      padding: theme.spacing(0, 1),
    },
    costLabel: {
      color: theme.palette.blue.main,
    },
    popperBody: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(2),
      gap: theme.spacing(2),
    },
    attributesBox: {
      display: "flex",
      flexDirection: "column",
    },
    builderSuppliedChip: {
      height: 16,
      borderRadius: 4,
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.secondary.main,
      display: "flex",
      justifyContent: "flex-start",
      width: "fit-content",
    },
    strikeThrough: {
      textDecoration: "line-through",
      color: theme.palette.red.dark,
    },
  })
);

interface MaterialAttribute {
  id?: string | number;
  attributeId?: string | number;
  attributeName: string;
  value: string;
}

interface MaterialCategory {
  categoryName?: string;
  itemDescription?: string;
}

interface MaterialDocument {
  createdOn: string;
  referenceId: string;
  contentType: string;
  filename: string;
  title: string;
  modifiedOn: string;
  id: string;
  metadata: { title: string };
  tempURL: string;
  uri: string;
}

interface MaterialItem {
  id?: string | number;
  manufacturerPartNumber?: string;
  manufacturerListPrice?: number | null;
  cost?: number | null;
  netSalesPrice?: number | null;
  itemDescription?: string;
  category?: MaterialCategory;
  attributes?: MaterialAttribute[];
  document?: MaterialDocument;
}

interface MenuItem {
  label?: string;
  icon?: string;
  color?: string;
  action?: () => void;
}

interface ThinMaterialCardProps {
  item?: MaterialItem;
  menuData?: MenuItem[];
  leftAction?: ReactNode;
  isSuppliedByBuilder?: boolean;
  isLoading?: boolean;
  isRemoved?: boolean;
  cardStyle?: React.CSSProperties;
  showSalePrice?: boolean;
}
